import type { DriveDocumentListItem } from '@platform/documents';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Icon,
  ListItem,
  TextField,
  Typography,
} from '@shared/ui';
import React from 'react';
import googleDocsIconSvg from '../../../assets/icons/GoogleDocs.svg';
import { tokenService } from '../../../services/tokenService';

interface DriveDocumentSearchProps {
  personId: string;
  onDocumentsSelected: (docs: DriveDocumentListItem[]) => void;
  onLoadingChange?: (loading: boolean) => void;
  onErrorChange?: (error: string | null) => void;
  disabled?: boolean;
}

const DEFAULT_LIMIT = 15;
const SEARCH_DEBOUNCE_MS = 350;

type Option = DriveDocumentListItem;

const DriveDocumentSearch: React.FC<DriveDocumentSearchProps> = ({
  personId,
  onDocumentsSelected,
  onLoadingChange,
  onErrorChange,
  disabled = false,
}) => {
  const [open, setOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState('');
  const [options, setOptions] = React.useState<Option[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const requestIdRef = React.useRef(0);
  const fetchTimeoutRef = React.useRef<number | null>(null);

  const updateLoading = React.useCallback(
    (value: boolean) => {
      setLoading(value);
      onLoadingChange?.(value);
    },
    [onLoadingChange]
  );

  const updateError = React.useCallback(
    (value: string | null) => {
      setError(value);
      onErrorChange?.(value ?? null);
    },
    [onErrorChange]
  );

  const fetchDocuments = React.useCallback(
    async (query: string) => {
      if (!personId) {
        return;
      }

      const requestId = ++requestIdRef.current;
      updateLoading(true);
      updateError(null);

      try {
        const headers = await tokenService.buildAuthHeaders();
        const params = new URLSearchParams();
        params.set('limit', String(DEFAULT_LIMIT));
        if (query.trim()) {
          params.set('q', query.trim());
        }

        const url = `/rpc/v1/drive/users/${encodeURIComponent(personId)}/providers/google-drive/documents?${params.toString()}`;
        const response = await fetch(url, { headers });
        if (!response.ok) {
          throw new Error(`Drive search failed (${response.status})`);
        }

        const payload = (await response.json()) as { documents?: Array<Record<string, any>> };
        if (requestIdRef.current !== requestId) {
          return;
        }

        const docs: DriveDocumentListItem[] = Array.isArray(payload.documents)
          ? payload.documents.map((doc) => ({
              documentId: doc.documentId,
              name: doc.name,
              mimeType: doc.mimeType,
              webViewUrl: doc.webViewUrl,
              modifiedTime: doc.modifiedTime,
              relevanceScore:
                typeof doc.relevanceScore === 'number' ? doc.relevanceScore : undefined,
            }))
          : [];

        setOptions(docs);
      } catch (err) {
        if (requestIdRef.current !== requestId) {
          return;
        }
        const message = err instanceof Error ? err.message : 'Failed to search Drive documents';
        updateError(message);
        setOptions([]);
      } finally {
        if (requestIdRef.current === requestId) {
          updateLoading(false);
        }
      }
    },
    [personId, updateError, updateLoading]
  );

  React.useEffect(() => {
    if (!personId) {
      setOptions([]);
      return;
    }
    fetchDocuments('');
  }, [fetchDocuments, personId]);

  React.useEffect(() => {
    if (!open) {
      return;
    }

    if (fetchTimeoutRef.current) {
      window.clearTimeout(fetchTimeoutRef.current);
    }

    fetchTimeoutRef.current = window.setTimeout(() => {
      fetchDocuments(inputValue);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (fetchTimeoutRef.current) {
        window.clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, [inputValue, open, fetchDocuments]);

  const handleSelect = React.useCallback(
    (doc: Option | null) => {
      if (!doc) return;
      onDocumentsSelected([doc]);
      setInputValue('');
      setOpen(false);
    },
    [onDocumentsSelected]
  );

  return (
    <Box className="flex flex-col gap-1">
      <Autocomplete
        fullWidth
        disabled={disabled}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        options={options}
        loading={loading}
        value={null}
        filterOptions={(x) => x}
        inputValue={inputValue}
        onInputChange={(_, value, reason) => {
          if (reason === 'input') {
            setInputValue(value);
          }
        }}
        getOptionLabel={(option) => option?.name || ''}
        isOptionEqualToValue={(option, value) => option.documentId === value.documentId}
        noOptionsText={loading ? 'Searching…' : 'No documents found'}
        onChange={(_, value) => handleSelect(value)}
        renderOption={(props, option) => (
          <ListItem
            {...props}
            key={option.documentId}
            className="flex items-center justify-between gap-3"
          >
            <Box className="flex items-center gap-2 min-w-0">
              {option.mimeType === 'application/vnd.google-apps.document' ? (
                <img src={googleDocsIconSvg} alt="Google Docs" className="w-5 h-5 flex-shrink-0" />
              ) : (
                <Icon className="text-neutral-500">description</Icon>
              )}
              <Box className="flex flex-col min-w-0">
                <Typography variant="body2" noWrap>
                  {option.name}
                </Typography>
                {option.modifiedTime && (
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {new Date(option.modifiedTime).toLocaleString()}
                  </Typography>
                )}
              </Box>
            </Box>
            <Button
              size="small"
              variant="text"
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                handleSelect(option);
              }}
            >
              Add
            </Button>
          </ListItem>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Search Google Drive"
            placeholder="Browse your Drive or search by keyword"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={18} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      {error && (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default DriveDocumentSearch;
